import { Trash2 } from "lucide-react";
import { resolveRoleSelectValue } from "../../../utils/roleSelectOptions";
import PolicyRoleSelectOptions from "./PolicyRoleSelectOptions";
import { pwInput, pwLabel, pwSectionTitle } from "./policyWizardFieldStyles";

/**
 * Onboarding-style checklist mapping step.
 */
export default function PolicyChecklistStepSection({
  darkMode = false,
  policy,
  checklistMappings = [],
  description = "Additional tasks triggered on approval events.",
  emptyHint = "Add checklist items (e.g. Salary revision, Insurance enrollment, ID card, Asset allocation).",
  triggerEvents = [],
  departments = [],
  dueDateLogicOptions = [],
  policyRoleOptions = [],
  addChecklistItem,
  removeChecklistItem,
  updateChecklistItem,
  renderRowExtras,
}) {
  return (
    <div>
      <h4 className={pwSectionTitle(darkMode)}>Checklist Mapping</h4>
      <p className={`text-xs mb-3 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>{description}</p>
      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={addChecklistItem}
          className="px-3 py-2 text-sm font-medium text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))] border border-[hsl(var(--ds-border))] rounded-lg"
        >
          + Add checklist item
        </button>
      </div>
      <div className="space-y-3">
        {checklistMappings.map((item, idx) => (
          <div
            key={item.taskId || `checklist-${idx}`}
            className={`grid grid-cols-1 md:grid-cols-7 gap-2 p-3 border rounded-xl ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <div>
              <label className={pwLabel(darkMode, "xs")}>Trigger Event</label>
              <select
                value={item.triggerEvent}
                onChange={(e) => updateChecklistItem(idx, "triggerEvent", e.target.value)}
                className={pwInput(darkMode)}
              >
                {triggerEvents.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={pwLabel(darkMode, "xs")}>Department</label>
              <select
                value={item.department}
                onChange={(e) => updateChecklistItem(idx, "department", e.target.value)}
                className={pwInput(darkMode)}
              >
                {departments.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={pwLabel(darkMode, "xs")}>Checklist Item</label>
              <input
                type="text"
                value={item.name}
                onChange={(e) => updateChecklistItem(idx, "name", e.target.value)}
                placeholder="e.g. Salary revision"
                className={pwInput(darkMode)}
              />
            </div>
            <div>
              <label className={pwLabel(darkMode, "xs")}>Action Role</label>
              <select
                value={resolveRoleSelectValue(policyRoleOptions, item.actionRole)}
                onChange={(e) => updateChecklistItem(idx, "actionRole", e.target.value)}
                className={pwInput(darkMode)}
              >
                <PolicyRoleSelectOptions roleOptions={policyRoleOptions} storedValue={item.actionRole} />
              </select>
            </div>
            <div>
              <label className={pwLabel(darkMode, "xs")}>Mandatory</label>
              <select
                value={item.mandatory ? "yes" : "no"}
                onChange={(e) => updateChecklistItem(idx, "mandatory", e.target.value === "yes")}
                className={pwInput(darkMode)}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label className={pwLabel(darkMode, "xs")}>Due Date Logic</label>
              <select
                value={item.dueDateLogic}
                onChange={(e) => updateChecklistItem(idx, "dueDateLogic", e.target.value)}
                className={pwInput(darkMode)}
              >
                {dueDateLogicOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className={pwLabel(darkMode, "xs")}>Due Days</label>
                <input
                  type="number"
                  min={0}
                  value={item.dueDateDays ?? 7}
                  onChange={(e) =>
                    updateChecklistItem(idx, "dueDateDays", Number(e.target.value) || 0)
                  }
                  className={pwInput(darkMode)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeChecklistItem(idx)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                aria-label="Remove checklist item"
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {renderRowExtras?.({
              item,
              idx,
              policy,
              darkMode,
              updateChecklistItem,
              checklistMappings,
            })}
          </div>
        ))}
      </div>
      {checklistMappings.length === 0 && (
        <p className={`text-xs mt-2 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>{emptyHint}</p>
      )}
    </div>
  );
}
